import type { SubKrewe } from '~/shared/types/subkrewe.js'
import { buildSubKreweSearchText } from '~/shared/utils/subkreweSearch.js'
import {
  SEARCH_DEFAULT_LIMIT,
  SEARCH_MIN_LENGTH,
  isSearchableQuery,
  matchesSearchQuery,
  normalizeSearchText,
  tokenizeSearchQuery,
} from '~/shared/utils/searchText.js'

/**
 * Client-side SubKrewe search. The member counterpart is utils/memberSearch.ts,
 * which caches per-member blobs because the roster runs to thousands; the
 * SubKrewe collection is bounded in the hundreds, so this rebuilds blobs per
 * call rather than carrying a cache that would need invalidating.
 */

export interface SubKreweSearchOptions {
  limit?: number
  minLength?: number
  /** Include captain names in the match. Off by default — see SubKreweSearchFields. */
  includeCaptains?: boolean
}

function subkreweSearchText(subkrewe: SubKrewe, includeCaptains: boolean): string {
  return buildSubKreweSearchText({
    name: subkrewe.name,
    description: subkrewe.description,
    captains: includeCaptains ? subkrewe.captains : null,
  })
}

/** Picker semantics: nothing until the query is worth running, then a capped list. */
export function searchSubKrewes<T extends SubKrewe>(
  subkrewes: T[],
  query: string,
  options: SubKreweSearchOptions = {},
): T[] {
  const {
    limit = SEARCH_DEFAULT_LIMIT,
    minLength = SEARCH_MIN_LENGTH,
    includeCaptains = false,
  } = options

  if (!isSearchableQuery(query, minLength)) return []

  const tokens = tokenizeSearchQuery(query)
  const results: T[] = []
  for (const subkrewe of subkrewes) {
    if (!matchesSearchQuery(subkreweSearchText(subkrewe, includeCaptains), tokens)) continue
    results.push(subkrewe)
    if (results.length >= limit) break
  }
  return results
}

/** List semantics: an empty query means every row, and matches are uncapped. */
export function filterSubKrewesByQuery<T extends SubKrewe>(
  subkrewes: T[],
  query: string,
  options: Pick<SubKreweSearchOptions, 'includeCaptains'> = {},
): T[] {
  if (!isSearchableQuery(query, 1)) return subkrewes

  const tokens = tokenizeSearchQuery(query)
  const includeCaptains = options.includeCaptains ?? false
  return subkrewes.filter(subkrewe =>
    matchesSearchQuery(subkreweSearchText(subkrewe, includeCaptains), tokens),
  )
}

/**
 * Rank a search hit by how well the SubKrewe's *name* matches, best first:
 * exact, then name prefix, then any word prefix, then a bare substring.
 * Compares normalized text, so `renee` ranks `Renée's Krewe` as a prefix hit
 * rather than dropping it to the bottom.
 */
export function subkreweNameMatchRank(name: string | null | undefined, query: string): number {
  const haystack = normalizeSearchText(name)
  const needle = normalizeSearchText(query)
  if (!needle || !haystack) return 4

  if (haystack === needle) return 0
  if (haystack.startsWith(needle)) return 1
  if (haystack.split(' ').some(word => word.startsWith(needle))) return 2
  if (haystack.includes(needle)) return 3
  return 4
}
